// Custom Element senza Shadow DOM: non crea markup, si aggancia a quello esistente.
class SmartDropdown extends HTMLElement {
  constructor() {
    super();
    this._initialized = false;
    this._suppressDispatch = false;
  }

  connectedCallback() {
    if (this._initialized) return;
    this._initialized = true;

    // Hooks al markup già presente
    this.trigger = this.querySelector(".sd-trigger");
    this.search = this.querySelector(".sd-search"); // sostituisce il trigger quando aperta
    this.panel = this.querySelector(".sd-panel");
    this.list = this.querySelector(".sd-list");
    this.hiddenInput = this.querySelector(".sd-input");
    this.labelSpan = this.querySelector(".sd-label");

    // Stato
    this._open = false;
    this._activeIndex = -1;
    this._options = Array.from(this.querySelectorAll(".sd-option"));
    this._value = this.getAttribute("value") || this.hiddenInput?.value || "";

    // Init UI (placeholder vs value)
    const placeholder = this.getAttribute("placeholder") || "Select…";
    if (this._value) {
      const found = this._options.find((o) => o.dataset.value === this._value);
      if (found) {
        this._setTriggerLabel(found.textContent.trim());
        this._markSelected(found.dataset.value);
        this.classList.remove("is-placeholder");
      } else {
        this._setTriggerLabel(placeholder);
        this.classList.add("is-placeholder");
      }
    } else {
      this._setTriggerLabel(placeholder);
      this.classList.add("is-placeholder");
    }
    if (this.hiddenInput) this.hiddenInput.value = this._value;

    const searchPlaceholder = this.getAttribute("search-placeholder") || "Type to search…";
    if (this.search) this.search.setAttribute("placeholder", searchPlaceholder);

    this._bindEvents();
  }

  disconnectedCallback() {
    this._cleanup?.();
    this._initialized = false;
  }

  // API
  get value() {
    return this._value;
  }
  set value(v) {
    this.classList.add("is-placeholder");
    if (!v) {
      this._value = "";
      this._setTriggerLabel(this.getAttribute("placeholder") || "Select…");
      this._markSelected("");
      if (this.hiddenInput) this.hiddenInput.value = "";
      if (!this._suppressDispatch) this.dispatchEvent(new Event("change", { bubbles: true }));
      return;
    }

    const found = this._options.find((o) => o.dataset.value === v);
    if (!found) return;
    this._value = found.dataset.value;
    this._setTriggerLabel(found.textContent.trim());
    this._markSelected(this._value);
    this.classList.remove("is-placeholder");
    if (this.hiddenInput) this.hiddenInput.value = this._value;

    if (!this._suppressDispatch) {
      this.dispatchEvent(new Event("change", { bubbles: true }));
    }
  }

  open() {
    if (this._open) return;
    this.setAttribute("data-open", "");
    this._open = true;
    this._activeIndex = -1;
    this.trigger?.setAttribute("aria-expanded", "true");

    if (this.hasAttribute("search-on-click") && this.search) {
      this.search.value = "";
      this._applyFilter("");
      this.search.setAttribute("role", "combobox");
      this.search.setAttribute("aria-expanded", "true");
      if (this.list?.id) this.search.setAttribute("aria-controls", this.list.id);
      queueMicrotask(() => this.search.focus());
    } else {
      this.list?.focus();
    }

    // chiusura esterna / ESC
    this._onDocClick = (e) => {
      if (!this.contains(e.target)) this.close();
    };
    this._onDocKey = (e) => {
      if (e.key === "Escape") this.close();
    };
    document.addEventListener("pointerdown", this._onDocClick);
    document.addEventListener("keydown", this._onDocKey);
  }

  close() {
    if (!this._open) return;
    this.removeAttribute("data-open");
    this._open = false;
    this.trigger?.setAttribute("aria-expanded", "false");
    this.search?.removeAttribute("role");
    this.search?.removeAttribute("aria-expanded");
    this.search?.removeAttribute("aria-controls");
    this.trigger?.focus();
    document.removeEventListener("pointerdown", this._onDocClick);
    document.removeEventListener("keydown", this._onDocKey);
  }

  refresh() {
    this._options = Array.from(this.querySelectorAll(".sd-option"));
  }

  setOptions(items = []) {
    const list = this.querySelector(".sd-list");
    if (!list) return;

    list.innerHTML = "";
    for (const { label, id } of items) {
      const li = document.createElement("li");
      li.className = "sd-option";
      li.setAttribute("role", "option");
      li.dataset.value = id ?? label;
      li.textContent = label;
      list.appendChild(li);
    }

    const old = this._suppressDispatch;
    this._suppressDispatch = true;
    this.value = "";
    this._suppressDispatch = old;
    this.refresh();
  }

  reset() {
    const old = this._suppressDispatch;
    this._suppressDispatch = true;
    this.value = "";
    this._suppressDispatch = old;
    const list = this.querySelector(".sd-list");
    if (list) list.innerHTML = "";
    this.refresh();
  }

  // Interni
  _bindEvents() {
    this.trigger?.addEventListener("click", () => (this._open ? this.close() : this.open()));
    this.trigger?.addEventListener("keydown", (e) => this._onTriggerKeydown(e));

    this.search?.addEventListener("input", () => this._applyFilter(this.search.value));
    this.search?.addEventListener("keydown", (e) => this._onSearchKeydown(e));

    this.list?.addEventListener("keydown", (e) => this._onListKeydown(e));
    this.list?.addEventListener("click", (e) => {
      const li = e.target.closest(".sd-option");
      if (li) this._chooseByElement(li);
    });

    // cleanup
    this._cleanup = () => {
      document.removeEventListener("pointerdown", this._onDocClick);
      document.removeEventListener("keydown", this._onDocKey);
    };
  }

  _setTriggerLabel(txt) {
    if (this.labelSpan) this.labelSpan.textContent = txt;
  }

  _markSelected(val) {
    this._options.forEach((el) => el.removeAttribute("aria-pressed"));
    const el = this._options.find((o) => o.dataset.value === val);
    if (el) el.setAttribute("aria-pressed", "true");
  }

  _chooseByElement(li) {
    const val = li.dataset.value;
    const lab = li.textContent.trim();
    this._value = val;
    this._setTriggerLabel(lab);
    this._markSelected(val);
    this.classList.remove("is-placeholder");
    if (this.hiddenInput) this.hiddenInput.value = this._value;
    this.dispatchEvent(new Event("change", { bubbles: true }));
    this.close();
  }

  _applyFilter(q) {
    const needle = (q || "").trim().toLowerCase();
    this._options.forEach((opt) => {
      const match = !needle || opt.textContent.toLowerCase().includes(needle);
      opt.toggleAttribute("hidden", !match);
    });
    this._activeIndex = -1;
    this._clearCurrent();
  }

  _moveActive(delta) {
    const visibles = this._options.filter((o) => !o.hasAttribute("hidden"));
    if (!visibles.length) return;
    this._activeIndex = (this._activeIndex + delta + visibles.length) % visibles.length;
    this._clearCurrent();
    const current = visibles[this._activeIndex];
    current.setAttribute("aria-current", "true");
    current.scrollIntoView({ block: "nearest" });
  }

  _clearCurrent() {
    this._options.forEach((el) => el.removeAttribute("aria-current"));
  }

  _commitActive() {
    const visibles = this._options.filter((o) => !o.hasAttribute("hidden"));
    const el = visibles[this._activeIndex];
    if (el) this._chooseByElement(el);
  }

  _onTriggerKeydown(e) {
    if (e.key === "ArrowDown" || e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      this.open();
    }
  }
  _onSearchKeydown(e) {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      this._moveActive(1);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      this._moveActive(-1);
    } else if (e.key === "Enter") {
      e.preventDefault();
      this._commitActive();
    } else if (e.key === "Escape") {
      e.preventDefault();
      this.close();
    }
  }
  _onListKeydown(e) {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      this._moveActive(1);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      this._moveActive(-1);
    } else if (e.key === "Enter") {
      e.preventDefault();
      this._commitActive();
    } else if (e.key === "Escape") {
      e.preventDefault();
      this.close();
    }
  }
}

if (!customElements.get("smart-dropdown")) {
  customElements.define("smart-dropdown", SmartDropdown);
}
